import { useEffect, useRef } from 'react';

// OGC-531: renovation types, names and order come from the authorized catalog.
// Keys follow the database constraint on curated_renovation_types.key.
const RENOVATION_TYPE_KEY_PATTERN = /^[a-z0-9]+(?:_[a-z0-9]+)*$/;
const MAX_RENOVATION_TYPE_KEY_LENGTH = 80;
const MAX_SELECTED_RENOVATION_TYPES = 40;

export function validRenovationTypeKey(value) {
  return typeof value === 'string'
    && value.length <= MAX_RENOVATION_TYPE_KEY_LENGTH
    && RENOVATION_TYPE_KEY_PATTERN.test(value);
}

function finiteOrder(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : Number.MAX_SAFE_INTEGER;
}

// Configured type order, then name, then the stable key as a deterministic
// tie-break.
export function compareRenovationTypes(left, right) {
  return finiteOrder(left?.displayOrder) - finiteOrder(right?.displayOrder)
    || String(left?.displayName || '').localeCompare(String(right?.displayName || ''))
    || String(left?.key || '').localeCompare(String(right?.key || ''));
}

// Configured creative order, then name, then the stable version id.
export function compareCatalogCreatives(left, right) {
  return finiteOrder(left?.package?.displayOrder) - finiteOrder(right?.package?.displayOrder)
    || String(left?.package?.displayName || left?.package?.packageKey || '')
      .localeCompare(String(right?.package?.displayName || right?.package?.packageKey || ''))
    || String(left?.version?.id || '').localeCompare(String(right?.version?.id || ''));
}

// Groups already-validated Published entries by renovation type in configured
// order. Keys are unique per type, so a key claimed by two type ids fails
// closed. `sameChoice` decides which entries are duplicates within a type.
export function groupPublishedEntries(entries, sameChoice = (left, right) => left.version.id === right.version.id) {
  const keyOwners = new Map();
  for (const entry of entries) {
    const owners = keyOwners.get(entry.renovationType.key) || new Set();
    owners.add(entry.renovationType.id);
    keyOwners.set(entry.renovationType.key, owners);
  }
  const groups = new Map();
  for (const entry of entries) {
    const type = entry.renovationType;
    if (keyOwners.get(type.key).size > 1) continue;
    let group = groups.get(type.id);
    if (!group) {
      const name = typeof type.displayName === 'string' ? type.displayName.trim() : '';
      group = { id: type.id, key: type.key, displayName: name || type.key, displayOrder: type.displayOrder, choices: [] };
      groups.set(type.id, group);
    }
    if (group.choices.some((choice) => sameChoice(choice, entry))) continue;
    group.choices.push(entry);
  }
  return [...groups.values()]
    .sort(compareRenovationTypes)
    .map((group) => ({ ...group, choices: group.choices.sort(compareCatalogCreatives) }));
}

// Route query types are validated for shape only; the screen that loads the
// catalog decides which of them are current offerings.
export function parseRenovationTypeKeys(value) {
  const keys = [];
  for (const raw of String(value || '').split(',')) {
    const key = raw.trim();
    if (!validRenovationTypeKey(key) || keys.includes(key)) continue;
    keys.push(key);
    if (keys.length === MAX_SELECTED_RENOVATION_TYPES) break;
  }
  return keys;
}

// Membership, names and order that customers can see. A refresh that changes
// none of them keeps the current view, open previews and expanded sections.
export function catalogGroupsSignature(groups) {
  return JSON.stringify((Array.isArray(groups) ? groups : []).map((group) => [
    group.id,
    group.key,
    group.displayName,
    (group.choices || []).map((entry) => [
      entry.version?.id,
      entry.package?.displayName || '',
      entry.version?.headline || '',
      entry.version?.primaryText || '',
    ]),
  ]));
}

// Platform catalog edits in this tab and other tabs notify open customer views.
const CATALOG_CHANNEL = 'beyondleads:curated-catalog';
const listeners = new Set();
let channel = null;

function catalogChannel() {
  if (channel !== null) return channel || null;
  try {
    channel = typeof BroadcastChannel === 'function' ? new BroadcastChannel(CATALOG_CHANNEL) : false;
    if (channel) channel.onmessage = () => listeners.forEach((listener) => listener());
  } catch {
    channel = false;
  }
  return channel || null;
}

export function notifyCuratedCatalogChanged() {
  listeners.forEach((listener) => listener());
  try { catalogChannel()?.postMessage({ type: 'changed' }); } catch { /* unavailable */ }
}

export const CATALOG_REFRESH_INTERVAL_MS = 60_000;
export const CATALOG_REFRESH_TIMEOUT_MS = 20_000;

// Background catalog reads are bounded, so a request that never settles cannot
// hold the in-flight guard and disable every later refresh, and an expired
// session does not redirect away from unsaved work.
export function readTenantCatalogInBackground(apiClient, requestOptions, timeoutMs = CATALOG_REFRESH_TIMEOUT_MS) {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  return apiClient.get('/api/saas/catalog', {
    ...requestOptions,
    redirectOnUnauthorized: false,
    ...(controller ? { signal: controller.signal } : {}),
  })
    .finally(() => { if (timer) clearTimeout(timer); });
}

// Revalidate on entry (the caller's own load), window focus, tab return, a
// catalog change signal, and every minute while the page stays visible.
export function useCuratedCatalogRefresh(onRefresh, { enabled = true, intervalMs = CATALOG_REFRESH_INTERVAL_MS } = {}) {
  const latest = useRef(onRefresh);
  latest.current = onRefresh;
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';
    const refresh = () => { if (visible()) latest.current?.(); };
    const onVisibility = () => { if (visible()) latest.current?.(); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisibility);
    listeners.add(refresh);
    catalogChannel();
    const timer = Number.isFinite(intervalMs) && intervalMs > 0 ? setInterval(refresh, intervalMs) : null;
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisibility);
      listeners.delete(refresh);
      if (timer) clearInterval(timer);
    };
  }, [enabled, intervalMs]);
}
